/**
 * The one drag-and-drop file target used by every upload in the app.
 *
 * Customer review 11 Sep (user request: "everywhere document upload is used
 * allow for drag and drop please, it says it is but doesn't work"). Uploads
 * had grown ~40 hand-rolled variants: some advertised drag-and-drop with no
 * drop handler at all, some forgot `preventDefault()` on dragover (so the
 * browser opened the file instead), none applied `accept` to a drop. This
 * component owns all of it so every upload behaves identically:
 *
 * - dragenter / dragover / dragleave / drop all `preventDefault()`; a depth
 *   counter keeps the highlight steady while the pointer crosses children;
 * - drops are held to the same `accept`, `maxSize` and `multiple` rules as
 *   the picker — refusals get a toast naming the file and the reason;
 * - click, Enter and Space open the picker (clicks that land on a control
 *   inside the zone are left to that control — but keep buttons such as
 *   "Remove" outside: the zone is itself a button);
 * - `disabled` / `busy` block both paths.
 *
 * Callers keep their upload logic: they receive `onFiles(File[])`, the same
 * list their input's onChange used to produce.
 *
 * @example
 * <FileDropZone accept=".pdf,.docx" maxSize={10 * 1024 * 1024} multiple
 *   hint="PDF or Word, max 10MB" onFiles={(files) => upload(files)} />
 */
import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { Loader2, Upload, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { isFileDrag, partitionFiles } from './fileDropValidation';

/** Prompt shown by every zone that takes several files. */
export const FILE_DROP_PROMPT = 'Drag and drop files here, or click to browse';
/** Prompt shown by every zone that takes exactly one file. */
export const FILE_DROP_PROMPT_SINGLE = 'Drag and drop a file here, or click to browse';

/** Clicks on these inside the zone belong to them, not to the picker. */
const INTERACTIVE_CHILD = 'button, a, input, select, textarea, label, [role="button"], [data-no-browse]';

export interface FileDropZoneProps {
  /** Receives the files that passed the accept / size / multiple checks. */
  onFiles: (files: File[]) => void;
  /** Same syntax as the input attribute: ".pdf,.docx,image/*". Enforced on drop too. */
  accept?: string;
  /** Per-file size cap in bytes. */
  maxSize?: number;
  /** Allow more than one file per drop / pick. */
  multiple?: boolean;
  disabled?: boolean;
  /** An upload is running: spinner, "Uploading…" (or `label`), no new files taken. */
  busy?: boolean;
  /** Replaces the standard prompt — for progress text while busy, or a blocked state. */
  label?: ReactNode;
  /** Secondary line: the accepted types / size limit. */
  hint?: ReactNode;
  icon?: LucideIcon;
  /** `default` is a roomy centred box; `compact` a single row for forms. */
  size?: 'default' | 'compact';
  /** Extra non-interactive content under the prompt (e.g. the chosen file's name). */
  children?: ReactNode;
  className?: string;
  /** Lets a button elsewhere open this zone's picker. */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** id for the hidden input (label htmlFor / tests). */
  inputId?: string;
  /** data-testid for the hidden input itself (tests that drive a real input). */
  inputTestId?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Drag-and-drop + click-to-browse file target.
 *
 * @param props - See FileDropZoneProps
 * @returns The zone element (it renders its own hidden file input)
 */
export default function FileDropZone({
  onFiles,
  accept,
  maxSize,
  multiple = false,
  disabled = false,
  busy = false,
  label,
  hint,
  icon,
  size = 'default',
  children,
  className,
  inputRef,
  inputId,
  inputTestId,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: FileDropZoneProps) {
  const ownInputRef = useRef<HTMLInputElement>(null);
  const input = inputRef ?? ownInputRef;
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const blocked = disabled || busy;

  const deliver = (files: File[]) => {
    if (files.length === 0 || blocked) return;
    if (!multiple && files.length > 1) {
      toast.error(`Only one file can be added here — you dropped ${files.length}.`);
      return;
    }
    const { accepted, rejected } = partitionFiles(files, { accept, maxSize });
    rejected.forEach(({ file, reason }) => toast.error(`${file.name}: ${reason}`));
    if (accepted.length > 0) onFiles(accepted);
  };

  const openPicker = () => {
    if (!blocked) input.current?.click();
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    const hit = (e.target as HTMLElement).closest(INTERACTIVE_CHILD);
    if (hit && hit !== e.currentTarget) return;
    openPicker();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  // stopPropagation on all four keeps page-level listeners (the Document
  // Manager's whole-page drop, the window guard) from handling the same drop.
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    if (blocked) return;
    dragDepth.current += 1;
    if (dragDepth.current === 1) setIsDragging(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e.dataTransfer)) return;
    // Without this the browser navigates to the dropped file.
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = blocked ? 'none' : 'copy';
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e.dataTransfer)) return;
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragging(false);
    deliver(Array.from(e.dataTransfer.files || []));
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Cleared so picking the same file again still fires onChange.
    e.target.value = '';
    deliver(files);
  };

  const Icon = busy ? Loader2 : (icon ?? Upload);
  const prompt = label ?? (busy ? 'Uploading…' : (multiple ? FILE_DROP_PROMPT : FILE_DROP_PROMPT_SINGLE));
  const compact = size === 'compact';
  const iconClass = cn(
    isDragging ? 'text-primary' : 'text-muted-foreground',
    busy && 'animate-spin',
  );

  return (
    <div
      role="button"
      tabIndex={blocked ? -1 : 0}
      aria-disabled={blocked || undefined}
      aria-label={ariaLabel}
      data-file-drop-zone=""
      data-dragging={isDragging ? 'true' : undefined}
      data-testid={testId}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'rounded-lg border-2 border-dashed outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        compact ? 'px-3 py-2.5 text-left' : 'p-6 text-center',
        blocked
          ? 'cursor-not-allowed border-muted-foreground/20 opacity-60'
          : isDragging
            ? 'cursor-copy border-primary bg-primary/10 ring-2 ring-primary/30'
            : 'cursor-pointer border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30',
        className,
      )}
    >
      <input
        ref={input}
        id={inputId}
        data-testid={inputTestId}
        type="file"
        className="hidden"
        tabIndex={-1}
        accept={accept}
        multiple={multiple}
        disabled={blocked}
        onChange={handleInputChange}
        // The input sits inside the zone: its own click would bubble back into
        // handleClick and re-enter input.click(), which Chrome suppresses.
        onClick={(e) => e.stopPropagation()}
      />
      {compact ? (
        <div className="flex items-center gap-3">
          <Icon className={cn('h-5 w-5 shrink-0', iconClass)} />
          <div className="min-w-0">
            <p className="text-sm font-medium">{prompt}</p>
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          </div>
        </div>
      ) : (
        <>
          <Icon className={cn('mx-auto mb-2 h-8 w-8', iconClass)} />
          <p className="text-sm font-medium">{prompt}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </>
      )}
      {children && <div className={compact ? 'mt-2' : 'mt-3'}>{children}</div>}
    </div>
  );
}
